/**
 * JWT claims minted by the Cloudflare Worker after the Discord OAuth code flow.
 *
 * The Worker is the only party that signs; the sidecar and the SPA only ever verify or
 * read. Keep this file free of crypto - it is the contract, not the implementation.
 */
// The one import: `isUuidText` is the canonical spelling of a player uuid in this package and
// the `uuid` claim below has to be exactly that shape. A second regex here would be a second
// answer to "what is a player uuid" for the same wire format.
import { isUuidText } from './grants';

/**
 * Editor roles, ordered least to most privileged. The ordering is the whole point: it is
 * what {@link roleAtLeast} compares, so adding a role means inserting it in the right
 * place here and nowhere else.
 *
 * WHAT EACH TIER IS FOR, in one line each, because "viewer" and "player" are the pair a
 * reader will get wrong:
 *   - `unlinked` NO ROWS AT ALL. A Discord account that authenticated, is a member of the
 *               guild, holds no staff role and is bound to no Minecraft account. It exists so
 *               that the one thing such a person NEEDS to do is possible: present the code
 *               `/link` printed in game. {@link POSTGRES_READ_ROLE_FOR} maps it onto no
 *               Postgres role at all, so there is no read plane behind it — see that map.
 *   - `player`  ONE PERSON'S OWN ROWS. Not staff. Earned by binding a Discord account to a
 *               Minecraft account with `/link` in game, never by holding a guild role. The
 *               token carries {@link EditorJwtClaims.uuid} and the DATABASE is what narrows
 *               the rows to it (Postgres role `web_player`, schema `web_self`, RLS on every
 *               base table — see web/deploy/postgres-init/web-roles.sql section 5).
 *   - `viewer`  STAFF WHO MAY LOOK AND NOT TOUCH: every player's rows, the grant history and
 *               the audit log, and no write anywhere. It is a MODERATION tier — the answer to
 *               "who took that reward", "where did this player log out" — and it was never
 *               the self-service tier, which is exactly why `player` had to exist rather than
 *               `viewer` being narrowed. Granted by a Discord guild role.
 *   - `editor`  the config bundle: `/cdim editor` sessions, schema forms, the menu editor.
 *   - `admin`   may move value — {@link GRANT_MIN_ROLE} — i.e. file reward grant intents.
 *
 * INSERTING A TIER AT INDEX 0 WIDENS EVERY `roleAtLeast(held, …)` CHECK BY CONSTRUCTION, so
 * every comparison site in the repo is re-read when one lands. `player` was the first; there
 * were none affected, because the tier gates are `roleAtLeast(_, 'editor')` (the session store)
 * and `roleAtLeast(_, GRANT_MIN_ROLE)` (the grant form and the grant route), both of which mean
 * more than staff. What DID need saying explicitly is everywhere that checked only "is this
 * token valid" and meant "is this staff" — the read proxy's staff-only views and the SPA's audit
 * page — and those say {@link isStaff}. `unlinked` was the second, and this is why it is a TIER
 * rather than a flag: every gate in the system is already expressed against this ladder, so a
 * token that must be able to do less than a player does so by sitting below one, and the checks
 * that let a player through (`isStaff` is false, so is `role === 'player'`) keep it out with no
 * new predicate anywhere.
 */
export const EDITOR_ROLES = ['unlinked', 'player', 'viewer', 'editor', 'admin'] as const;

export type EditorRole = (typeof EDITOR_ROLES)[number];

export function isEditorRole(value: unknown): value is EditorRole {
  return typeof value === 'string' && (EDITOR_ROLES as readonly string[]).includes(value);
}

/**
 * The floor for "this is a member of staff", as opposed to a player looking at their own
 * rows. Spelled once so that a sixth tier cannot make some call sites mean something else.
 */
export const STAFF_MIN_ROLE: EditorRole = 'viewer';

/** True for staff tiers and false for `player` and `unlinked`. See {@link STAFF_MIN_ROLE}. */
export function isStaff(role: EditorRole): boolean {
  return roleAtLeast(role, STAFF_MIN_ROLE);
}

/**
 * The tiers a Discord GUILD ROLE can confer, which is every tier a database row confers instead.
 *
 * `player` is a statement about a binding the game server made, so it cannot be granted by adding
 * somebody to a guild role — see `worker/src/discord.ts`. `unlinked` is the absence of both, which
 * no role id can express either. Deriving this from EDITOR_ROLES rather than writing the three
 * names again means the role-map types and the mapping loop cannot drift from the ladder.
 */
export const STAFF_ROLES = EDITOR_ROLES.filter(isStaff) as readonly StaffRole[];

export type StaffRole = Exclude<EditorRole, 'player' | 'unlinked'>;

/**
 * True when `held` is at least as privileged as `required`.
 *
 * Roles are a total order rather than a permission set on purpose: the editor has four tiers
 * (own rows / look / change config / change grants) and a set-based model would invite
 * per-route bespoke checks. The order is NOT a claim that a `player` is a weak staff member:
 * it is a claim that anything staff may do, a player may not.
 */
export function roleAtLeast(held: EditorRole, required: EditorRole): boolean {
  return EDITOR_ROLES.indexOf(held) >= EDITOR_ROLES.indexOf(required);
}

/**
 * The signed session token's payload.
 *
 * `sub` is the Discord user id. The database DOES store one now — `player_profile.discord_id`,
 * migration 1.9, written only by the mod after a `/link` handshake it validated itself — and
 * that binding is what {@link uuid} is resolved through. Times are seconds since the epoch
 * (JWT convention, NOT milliseconds).
 */
export interface EditorJwtClaims {
  /** Discord user id (snowflake). */
  readonly sub: string;
  /** Discord username at mint time - display only, never an identity. */
  readonly name: string;
  /**
   * Role derived from the user's Discord guild roles at mint time, or `player` when a `/link`
   * binding answered instead, or `unlinked` when neither did and they are in the guild.
   */
  readonly role: EditorRole;
  /**
   * THE MINECRAFT PLAYER THIS TOKEN IS SCOPED TO. Present if and only if `role` is `player`.
   *
   * Put here by the Worker after resolving `sub` through `web.player_directory.discord_id`,
   * copied by the sidecar into the one-minute PostgREST token, and read by Postgres out of
   * `request.jwt.claims` as the `uuid` claim every policy in web-roles.sql section 5 keys on.
   * So this field is not a hint to the UI: it is the parameter of the database's row filter,
   * and the signature is what stops a player choosing a different one.
   *
   * ABSENT ON A STAFF TOKEN, and {@link isEditorJwtClaims} enforces both halves of that. A
   * staff token carrying one would be a claim with no meaning (staff read the unfiltered
   * `web` schema), and a player token without one would name no player at all — which the
   * database answers with zero rows, but which the sidecar refuses outright so the failure
   * says what it is.
   */
  readonly uuid?: string;
  /** Issuer - the Worker's configured origin. */
  readonly iss: string;
  /** Audience - the sidecar that will accept this token. */
  readonly aud: string;
  /** Issued at, epoch seconds. */
  readonly iat: number;
  /** Expiry, epoch seconds. Short lived by design; the SPA re-runs the OAuth flow. */
  readonly exp: number;
}

/** Lifetime the Worker stamps on a freshly minted token. */
export const EDITOR_JWT_TTL_SECONDS = 15 * 60;

/**
 * How the Worker hands the freshly minted token to the browser: in the URL FRAGMENT of
 * the post-login redirect, never in the query string. Fragments are not sent to servers,
 * so the credential stays out of access logs, out of `Referer`, and out of whatever the
 * Pages edge records.
 *
 * These two keys are the contract between `worker/src/index.ts` (which writes them) and
 * `app/src/auth/session.ts` (which reads them). They live here because a contract with
 * exactly two parties and two spellings will eventually be spelled two ways.
 */
export const TOKEN_FRAGMENT_KEY = 'dc_token';

/** Stable machine-readable reason a sign-in was refused. See the SPA for the wording. */
export const ERROR_FRAGMENT_KEY = 'dc_error';

/**
 * The refusal a Discord account with no staff role, no `/link` binding AND NO GUILD MEMBERSHIP
 * gets. All three, which is narrower than it once was and deliberately so.
 *
 * A guild member in that position is no longer refused: they are handed an `unlinked` token,
 * because "run `/link` in game and type the code here" is an instruction they cannot carry out
 * without a session — the claim route needs one — and a refusal that names an impossible remedy
 * is worse than no advice at all. What survives this code is the stranger: a Discord account
 * that is not in this server's guild and that the game server has never bound to a player. There
 * is nothing for them here and nothing they can do on the site to change that, so they are told
 * both halves.
 *
 * Still distinct from `not_authorised`, and still for the original reason: `not_authorised` means
 * "you are not staff and pressing this again will not help", while this one names something the
 * reader can act on — in game, and in the guild.
 */
export const AUTH_ERROR_NOT_LINKED = 'not_linked';

/**
 * The refusal when the binding could not be LOOKED UP - the sidecar or its database was
 * unreachable. Not the same as "you are not linked": nobody has learned anything about this
 * account, and trying again later is the right advice.
 */
export const AUTH_ERROR_BINDING_UNAVAILABLE = 'binding_unavailable';

/**
 * THE ACTUAL CEILING.
 *
 * An {@link EditorRole} is a UX affordance: it decides what the SPA draws. What a request
 * may actually read is decided by the Postgres role the sidecar switches to, whose grants
 * are set in `web/deploy/postgres-init/web-roles.sql` and are enforced by the database
 * whatever the browser believes. This map is the single place the translation happens.
 *
 * THE THREE STAFF TIERS READ AS `web_read` AND THE PLAYER TIER DOES NOT, and that split is
 * the line in this file with teeth. Staff share one curated read plane because the
 * distinction between looking, configuring and granting begins at the WRITE path; a player
 * shares nothing with them. `web_player` holds no usage on schema `web` at all, so a
 * player-tier request cannot NAME `web.audit_log` however the browser or a widened route
 * asks for it, and the rows it can name are filtered one by one against its own `uuid` claim.
 *
 * MAPPING `player` ONTO `web_read` WOULD UNDO THE WHOLE PLAYER TIER in one word: every other
 * player's homes, bases and logout coordinates, plus the grant and audit history. The map is
 * total (`Record<EditorRole, …>`) so a future tier cannot be added without the compiler
 * asking which of the two it is.
 *
 * `unlinked` IS `null`, WHICH IS A THIRD ANSWER AND NOT A MISSING ONE. There is no Postgres role
 * that reads nothing, so inventing one would mean creating a role, granting it usage on a schema
 * and then trusting the empty grant list — three chances to be wrong for a tier whose whole
 * purpose is to reach ONE write route (`/v1/link/claim`). `null` means the read proxy has nothing
 * to switch to and refuses before it mints an upstream token at all, and the type
 * (`string | null`) is what forces every reader to have an answer for that case rather than
 * interpolating `undefined` into a `set role` and finding out later.
 */
export const POSTGRES_READ_ROLE_FOR: Readonly<Record<EditorRole, string | null>> = {
  unlinked: null,
  player: 'web_player',
  viewer: 'web_read',
  editor: 'web_read',
  admin: 'web_read',
};

/**
 * THE SCHEMA those reads are served from - the other half of the ceiling above.
 *
 * PostgREST exposes both (`db-schemas = "web, web_self"`) and chooses per request from the
 * `Accept-Profile` header the sidecar sends. `web` is the staff plane the mod's migrations
 * create; `web_self` is the player plane web-roles.sql section 5 creates, with the same eight
 * view names and column-for-column the same shape, each filtered to the caller's own `uuid`.
 *
 * THE HEADER IS NOT THE ENFORCEMENT. Either role asking for the other's schema is a
 * `permission denied for schema` from Postgres — asserted in both directions by dev-stack.sh
 * — so this map decides which answer a legitimate request gets, not who may ask.
 *
 * `null` for `unlinked` for the same reason its Postgres role is null: there is no schema whose
 * answer would be right, and the request is refused before a profile has to be chosen.
 */
export const POSTGREST_SCHEMA_FOR: Readonly<Record<EditorRole, string | null>> = {
  unlinked: null,
  player: 'web_self',
  viewer: 'web',
  editor: 'web',
  admin: 'web',
};

/**
 * The Postgres role the INSERT-only grant path runs as (R6). Named here beside its read
 * twin so the two are read together; nothing calls it yet.
 */
export const POSTGRES_WRITE_ROLE = 'web_write';

/**
 * Claims PostgREST itself consumes - a different token with a different vocabulary from
 * {@link EditorJwtClaims}, minted by the sidecar for a single upstream request.
 *
 * `role` is a POSTGRES role name (`jwt-role-claim-key = ".role"` in postgrest.conf), not
 * an EditorRole, and `aud` must equal postgrest.conf's `jwt-aud`. The lifetime is seconds
 * because the token never leaves the sidecar's own outbound request.
 */
export interface PostgrestJwtClaims {
  readonly role: string;
  readonly aud: string;
  readonly exp: number;
  /**
   * The player this request is filtered to, present only on a `player`-tier exchange.
   *
   * PostgREST lands the whole verified payload in the `request.jwt.claims` GUC, so THIS is
   * the field every policy in web-roles.sql section 5 reads — `web_self.jwt_player_uuid()`
   * is `(request.jwt.claims::json ->> 'uuid')::uuid`, fail-closed. The name is shared
   * between a TypeScript interface and a Postgres function body and can therefore only be
   * changed in both at once.
   */
  readonly uuid?: string;
}

/** How long the sidecar's downstream PostgREST token lives. One request's worth. */
export const POSTGREST_JWT_TTL_SECONDS = 60;

/**
 * Narrowing guard for a decoded-but-not-yet-trusted payload. Signature verification is a
 * separate concern (Worker side); this only says the shape is right.
 */
export function isEditorJwtClaims(value: unknown): value is EditorJwtClaims {
  if (typeof value !== 'object' || value === null) return false;
  const c = value as Record<string, unknown>;
  return (
    typeof c['sub'] === 'string' &&
    typeof c['name'] === 'string' &&
    isEditorRole(c['role']) &&
    typeof c['iss'] === 'string' &&
    typeof c['aud'] === 'string' &&
    typeof c['iat'] === 'number' &&
    typeof c['exp'] === 'number' &&
    hasTheRightUuidForItsTier(c)
  );
}

/**
 * `uuid` present and well formed for a `player`, absent for staff. Both directions, because
 * both are meaningless states rather than harmless ones:
 *   - a player token WITHOUT a uuid names no player. The database answers that with zero rows
 *     (an absent claim filters, it does not widen), so this is belt on top of braces — but a
 *     token whose whole purpose is a row filter it does not carry is a bug, and it should be
 *     refused where it can still be told apart from "you have no homes".
 *   - a staff token WITH one is a filter nobody applies: staff read the unfiltered `web`
 *     schema, so accepting it would leave a claim in the token that reads like a scope and is
 *     not one. Refused so it cannot become load-bearing by accident.
 * A malformed uuid is refused rather than passed on to Postgres, which is the same decision
 * the SQL makes for the same reason: it can only have come from a forgery or a bug.
 */
function hasTheRightUuidForItsTier(claims: Record<string, unknown>): boolean {
  const uuid = claims['uuid'];
  if (claims['role'] === 'player') return isUuidText(uuid);
  return uuid === undefined;
}

/** True when `claims` is expired at `nowSeconds` (defaults to now). */
export function isExpired(claims: EditorJwtClaims, nowSeconds: number = Math.floor(Date.now() / 1000)): boolean {
  return claims.exp <= nowSeconds;
}
